import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../lib/utils';
import { resolveImageUrl } from '../../lib/imageUrl';
import { effectiveUnitPrice } from '../../utils/pricing';

export const CartDrawer = () => {
  const { cart, isOpen, setIsOpen, updateCartItem, removeFromCart, loading } = useCart();
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (!isOpen || cart.items.length === 0) {
      setRecommendations([]);
      return;
    }
    const productIds = cart.items.map(i => i.product_id).join(',');
    let cancelled = false;
    fetch(`/api/cart/recommendations?product_ids=${encodeURIComponent(productIds)}&limit=3`)
      .then(r => r.ok ? r.json() : { products: [] })
      .then(d => { if (!cancelled) setRecommendations(d.products || []); })
      .catch(() => { if (!cancelled) setRecommendations([]); });
    return () => { cancelled = true; };
  }, [isOpen, cart.items]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-50" 
        onClick={() => setIsOpen(false)}
        data-testid="cart-overlay"
      />

      {/* Drawer */}
      <div 
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-[#FCFBF9] z-50 flex flex-col"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E0DFDD]">
          <h2 className="font-serif text-2xl">Panier</h2>
          <button 
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-[#F4E3E1] rounded-full transition-colors"
            data-testid="close-cart-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {cart.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={48} className="text-[#E0DFDD] mb-4" />
              <p className="text-[#6B6A68] mb-4">Ton panier est vide</p>
              <Link 
                to="/shop"
                onClick={() => setIsOpen(false)}
                className="btn-primary px-6 py-3 text-sm font-medium"
                data-testid="continue-shopping-btn"
              >
                Découvrir la Collection
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.items.map((item) => (
                <div 
                  key={item.cart_item_id} 
                  className="flex gap-4"
                  data-testid={`cart-item-${item.cart_item_id}`}
                >
                  {/* Image */}
                  <div className="w-24 h-32 bg-[#F5F4F2] flex-shrink-0 overflow-hidden">
                    {item.product?.images?.[0]?.url && (
                      <img 
                        src={resolveImageUrl(item.product.images[0].url)} 
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 flex flex-col">
                    <Link 
                      to={`/product/${item.product_id}`}
                      onClick={() => setIsOpen(false)}
                      className="font-serif text-lg hover:text-[#D4AF37] transition-colors"
                    >
                      {item.product?.name}
                    </Link>
                    <p className="text-sm text-[#6B6A68] mt-1">
                      Taille: {item.size}
                      {item.color && <span> &middot; {item.color}</span>}
                    </p>
                    <p className="text-sm font-medium mt-1">
                      {formatPrice(effectiveUnitPrice(item.product, null))}
                    </p>

                    {/* Quantity */}
                    <div className="flex items-center gap-3 mt-auto">
                      <div className="flex items-center border border-[#E0DFDD]">
                        <button
                          onClick={() => updateCartItem(item.cart_item_id, item.quantity - 1)}
                          disabled={loading}
                          className="p-2 hover:bg-[#F4E3E1] transition-colors disabled:opacity-50"
                          data-testid={`decrease-qty-${item.cart_item_id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateCartItem(item.cart_item_id, item.quantity + 1)}
                          disabled={loading}
                          className="p-2 hover:bg-[#F4E3E1] transition-colors disabled:opacity-50"
                          data-testid={`increase-qty-${item.cart_item_id}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.cart_item_id)}
                        disabled={loading}
                        className="text-xs text-[#C87979] hover:underline disabled:opacity-50"
                        data-testid={`remove-item-${item.cart_item_id}`}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Frequently bought together */}
              {recommendations.length > 0 && (
                <div className="pt-6 border-t border-[#E0DFDD]" data-testid="cart-recommendations">
                  <p className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-4">
                    Souvent achetés ensemble
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {recommendations.map((rec) => {
                      const recPrice = effectiveUnitPrice(rec, null);
                      return (
                        <Link
                          key={rec.product_id}
                          to={`/product/${rec.product_id}`}
                          onClick={() => setIsOpen(false)}
                          className="group block"
                          data-testid={`recommendation-${rec.product_id}`}
                        >
                          <div className="aspect-[3/4] bg-[#F5F4F2] overflow-hidden mb-2">
                            {rec.images?.[0]?.url && (
                              <img
                                src={resolveImageUrl(rec.images[0].url)}
                                alt={rec.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            )}
                          </div>
                          <p className="text-[11px] leading-tight line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                            {rec.name}
                          </p>
                          <p className="text-xs font-medium mt-1">{formatPrice(recPrice)}</p>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.items.length > 0 && (
          <div className="border-t border-[#E0DFDD] p-6">
            {/* Free shipping progress */}
            {(() => {
              const FREE_SHIPPING_THRESHOLD = 10000;
              const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - cart.total);
              const progress = Math.min(100, (cart.total / FREE_SHIPPING_THRESHOLD) * 100);
              const unlocked = remaining === 0;
              return (
                <div
                  className={`mb-4 p-3 border ${unlocked ? 'bg-[#EFF3EC] border-[#A3B19B]' : 'bg-[#F4E3E1] border-[#E8C4C4]'}`}
                  data-testid="free-shipping-progress"
                >
                  <p className={`text-xs font-medium leading-relaxed ${unlocked ? 'text-[#5C7252]' : 'text-[#1A1514]'}`}>
                    {unlocked ? (
                      <>Livraison gratuite débloquée</>
                    ) : (
                      <>Plus que <span className="font-serif text-sm">{formatPrice(remaining)}</span> pour la livraison gratuite</>
                    )}
                  </p>
                  <div className="mt-2 h-1.5 w-full bg-white/60 overflow-hidden">
                    <div
                      className={`h-full transition-[width] duration-500 ease-out ${unlocked ? 'bg-[#A3B19B]' : 'bg-[#D4AF37]'}`}
                      style={{ width: `${progress}%` }}
                      data-testid="free-shipping-progress-bar"
                    />
                  </div>
                  {!unlocked && (
                    <Link
                      to="/shop?category=ensembles"
                      onClick={() => setIsOpen(false)}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#1A1514] hover:text-[#D4AF37] transition-colors uppercase tracking-wider"
                      data-testid="free-shipping-cta"
                    >
                      Compléter mon panier
                      <span aria-hidden="true">→</span>
                    </Link>
                  )}
                </div>
              );
            })()}

            <div className="flex justify-between items-center mb-4">
              <span className="text-[#6B6A68]">Sous-total</span>
              <span className="font-serif text-xl">{formatPrice(cart.total)}</span>
            </div>
            <p className="text-xs text-[#6B6A68] mb-4">
              Frais de livraison calculés à la caisse
            </p>
            <Link
              to="/checkout"
              onClick={() => setIsOpen(false)}
              className="btn-primary w-full py-4 text-center text-sm font-medium uppercase tracking-wider block"
              data-testid="checkout-btn"
            >
              Commander
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="w-full py-3 text-sm text-[#6B6A68] hover:text-[#1A1514] transition-colors mt-2"
              data-testid="continue-shopping-link"
            >
              Continuer mes achats
            </button>
          </div>
        )}
      </div>
    </>
  );
};
